import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  AlertTriangle,
  Users,
  Tag,
  Upload,
  Trash2,
  CircleDot,
  Pencil,
  FileText,
  User,
  ExternalLink
} from 'lucide-react';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { useNote } from '../contexts/NoteContext';
import { NoteFormValues } from '../types/interface';
import {
  getPriorityConfig,
  getStatusConfig,
  formatNoteDate,
  hexToRgba
} from '../mappers/noteMappers';
import NoteForm from './NoteForm';

const NoteDetails: FC = () => {
  const { t } = useTranslation(NsKey.Note);
  const navigate = useNavigate();
  const {
    selectedNote,
    isEditing,
    setEditingMode,
    updateNote,
    deleteNote
  } = useNote();

  if (!selectedNote) {
    return null;
  }

  const priority = getPriorityConfig(selectedNote.priority);
  const status = getStatusConfig(selectedNote.status);
  const displayDate = formatNoteDate(selectedNote.creationTime);

  const handleSave = (values: NoteFormValues) => {
    if (selectedNote.id) {
      updateNote(selectedNote.id, values);
    }
  };

  const handleCancel = () => {
    setEditingMode(false);
  };

  const handleDelete = () => {
    if (selectedNote.id) {
      deleteNote(selectedNote.id);
    }
  };

  if (isEditing) {
    return (
      <NoteForm
        note={selectedNote}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-6 py-5">
        {/* Header with title and edit button */}
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-[22px] font-semibold text-slate-800">
            {selectedNote.title}
          </h1>
          <Button color="primary" onClick={() => setEditingMode(true)}>
            <Pencil data-slot="icon" />
            {t('DETAILS.EDIT')}
          </Button>
        </div>

        {/* Metadata */}
        <div className="space-y-2 mb-5">
          {/* Date */}
          <div className="flex items-center gap-3 h-8">
            <Clock size={15} className="text-slate-400" />
            <span className="text-[13px] text-slate-400 w-20">
              {t('DETAILS.DATE')}
            </span>
            <span className="text-[13px] text-slate-600">{displayDate}</span>
          </div>

          {/* Priority */}
          {priority && (
            <div className="flex items-center gap-3 h-8">
              <AlertTriangle size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.PRIORITY')}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${priority.bgColor || ''} ${priority.textColor || ''}`}
                style={priority.style}
              >
                {priority.label}
              </span>
            </div>
          )}

          {/* Status */}
          {status && (
            <div className="flex items-center gap-3 h-8">
              <CircleDot size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.STATUS')}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${status.bgColor || ''} ${status.textColor || ''}`}
                style={status.style}
              >
                {status.label}
              </span>
            </div>
          )}

          {/* Person link */}
          {selectedNote.person && selectedNote.person.id && (
            <div className="flex items-center gap-3 h-8">
              <User size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.PERSON')}
              </span>
              <button
                onClick={() => navigate(`/mes-clients/${selectedNote.person?.id}`)}
                className="group flex items-center gap-1.5 text-[13px] text-primary hover:text-primary/80 transition-colors"
              >
                <span>{selectedNote.person.fullName}</span>
                <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          )}

          {/* Dossier (risque) lié — id = riskId, la route « dossier » résout l'étape */}
          {selectedNote.risk && selectedNote.risk.id && (
            <div className="flex items-center gap-3 h-8">
              <FileText size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.CONTRACT')}
              </span>
              <button
                onClick={() =>
                  // Contrat existant (actif/signé) : page contrat directe ; sinon la
                  // route « dossier » résout l'étape de process la plus avancée.
                  navigate(
                    selectedNote.risk?.contractId
                      ? `/mes-contrats/details/${selectedNote.risk.contractId}`
                      : `/simulation/dossier/${selectedNote.risk?.id}`
                  )
                }
                className="group flex items-center gap-1.5 text-[13px] text-primary hover:text-primary/80 transition-colors"
              >
                <span>{selectedNote.risk.identifier}</span>
                {selectedNote.risk.status && (
                  <span className="rounded bg-slate-100 px-1.5 py-px text-[11px] text-slate-500">
                    {selectedNote.risk.status}
                  </span>
                )}
                <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          )}

          {/* Mentions */}
          {selectedNote.mentions && selectedNote.mentions.length > 0 && (
            <div className="flex items-center gap-3 h-8">
              <Users size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.MENTIONS')}
              </span>
              <div className="flex items-center gap-2">
                {selectedNote.mentions.map((mention) => (
                  <div
                    key={mention.id}
                    className="flex items-center gap-2 px-2.5 py-1 bg-slate-100 rounded-full"
                  >
                    <div className="w-5 h-5 rounded-full bg-slate-400 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-white">
                        {mention.initials || mention.fullName?.split(' ').map(n => n[0]).join('') || '??'}
                      </span>
                    </div>
                    <span className="text-[13px] text-slate-600">
                      {mention.fullName || ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {selectedNote.tags && selectedNote.tags.length > 0 && (
            <div className="flex items-center gap-3 h-8">
              <Tag size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.TAGS')}
              </span>
              <div className="flex items-center gap-2">
                {selectedNote.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2.5 py-1 rounded-full text-xs font-medium"
                    style={
                      tag.color
                        ? {
                          backgroundColor: hexToRgba(tag.color, 0.15),
                          color: tag.color
                        }
                        : {
                          backgroundColor: '#f1f5f9',
                          color: '#64748b'
                        }
                    }
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Body content */}
        {selectedNote.content && (
          <div className="mb-5">
            <p className="text-[14px] leading-relaxed text-slate-500 whitespace-pre-wrap">
              {selectedNote.content}
            </p>
          </div>
        )}

        {/* Attachments count indicator */}
        {selectedNote.attachmentsCount !== undefined && selectedNote.attachmentsCount > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 text-slate-500">
              <Upload size={14} />
              <span className="text-[13px]">
                {selectedNote.attachmentsCount} pièce(s) jointe(s)
              </span>
            </div>
          </div>
        )}

        {/* Add attachments dropzone */}
        <div className="mb-5">
          <div className="border-2 border-dashed border-slate-200 rounded-xl py-5 px-4 flex flex-col items-center justify-center cursor-pointer hover:border-slate-300 transition-colors">
            <Upload size={20} className="text-slate-400 mb-2" />
            <span className="text-[13px] text-slate-400">
              {t('DETAILS.ADD_ATTACHMENTS')}
            </span>
          </div>
        </div>

        {/* Delete action */}
        <button
          onClick={handleDelete}
          className="flex items-center gap-2 text-slate-400 hover:text-red-500 transition-colors"
        >
          <Trash2 size={14} />
          <span className="text-[13px]">{t('DETAILS.DELETE_NOTE')}</span>
        </button>
      </div>
    </div>
  );
};

export default NoteDetails;
